<template>
    <div class="use-item-button">
        <v-tooltip v-if="usableItems.length > 0" top>
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    fab
                    x-small
                    dark
                    color="accent"
                    class="use-item-button__trigger btn-shadow"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="show = true"
                >
                    <v-icon size="20">mdi-flask</v-icon>
                </v-btn>
            </template>
            <span>{{ $t('pokemon.common.useItemTooltip') }}</span>
        </v-tooltip>

        <ResponsiveDialog
            v-if="usableItems.length > 0"
            v-model="show"
            :max-width="480"
            :full-height="false"
            :header-title="$t('pokemon.common.useItemDialogTitle', {name: pokemonName})"
            header-class="skin-window-header--oak"
            card-class="skin-card"
        >
            <div class="skin-window--oak use-item-dialog-bg">
                <v-card-text class="transparent03 use-item-dialog-content">
                    <div class="use-item-grid">
                        <div
                            v-for="group in groupedUsableItems"
                            :key="group.stackKey"
                            class="use-item-grid__item"
                            @click="confirmUse(group)"
                        >
                            <div class="use-item-grid__icon-wrap">
                                <v-img :src="require(`@/assets/pic${group.picture}`)" contain height="40" width="40" class="use-item-grid__icon"/>
                                <span v-if="group.count > 1" class="use-item-grid__count">×{{ group.count }}</span>
                            </div>
                            <span class="use-item-grid__name">{{ group.name }}</span>
                        </div>
                    </div>
                </v-card-text>
            </div>
        </ResponsiveDialog>

        <ConfirmDialog ref="confirmDialog"/>
    </div>
</template>

<script>
    import {mapActions} from 'vuex';
    import ResponsiveDialog from "@/components/utils/ResponsiveDialog";
    import ConfirmDialog from "@/components/utils/ConfirmDialog";

    export default {
        components: {ResponsiveDialog, ConfirmDialog},
        props: {
            pokemon: {type: Object, required: true},
            items: {type: Array, default: () => []},
        },
        data() {
            return {
                show: false,
                itemsList: [],
            }
        },
        computed: {
            pokemonName() {
                return this.pokemon.name || (this.pokemon.pokemonType && this.pokemon.pokemonType.name) || '';
            },
            // Jen spotrebni itemy - permanentni (vlak/kolo/baterka) se
            // pouzivaji na trenera jako celek, ne na konkretniho pokemona
            // (viz Items.vue) - tady se proto nikdy nenabizi. HEAL/HEAL_ENDANGERED
            // se navic nenabizi na plne zivoty - fullHeal() by beztak nic nezmenil
            // a item by se jen zbytecne spotreboval (viz UseItemAction.php).
            usableItems() {
                const isFullHp = this.pokemon.hp >= this.pokemon.maxHp;
                return this.itemsList.filter(item => !item.permanent
                    && !(isFullHp && (item.effectType === 'HEAL' || item.effectType === 'HEAL_ENDANGERED')));
            },
            groupedUsableItems() {
                const groups = {};
                const order = [];
                for (const item of this.usableItems) {
                    const key = `${item.category}|${item.picture}|${item.effectValue}`;
                    if (!groups[key]) {
                        groups[key] = {...item, stackKey: key, count: 0};
                        order.push(key);
                    }
                    groups[key].count++;
                }
                return order.map(key => groups[key]);
            },
        },
        watch: {
            items() {
                this.itemsList = this.items;
            },
        },
        created() {
            this.itemsList = this.items;
        },
        methods: {
            ...mapActions({
                useItemAction: 'trainer/useItem',
            }),
            async confirmUse(item) {
                let rlyUse = await this.$refs.confirmDialog.open(
                    this.$t('trainer.items.confirmUse', {itemName: item.name, pokemonName: this.pokemonName}),
                    {
                        oak: true,
                        noAutoConfirm: true,
                    }
                );
                if (!rlyUse) {
                    return;
                }
                let obj = {itemId: item.id, pokemonId: this.pokemon.id};
                this.useItemAction(obj).then(
                    () => {
                        // Lokalni odstraneni hned po uspechu, nezavisle na tom,
                        // kdy (jestli vubec) rodic prekresli `items` prop cerstvymi
                        // daty - stejny optimisticky vzor jako Items.vue.
                        this.itemsList = this.itemsList.filter(e => e.id !== item.id);
                        this.show = false;
                        this.$emit('used');
                    }
                ).catch((e) => {
                    this.$emit('use-error', e.message);
                });
            },
        },
    }
</script>

<style scoped>
.use-item-button {
    display: inline-flex;
}

.use-item-dialog-bg {
    display: flex;
    flex-direction: column;
    min-height: 0;
}
/* !important nutne - Vuetify ".v-dialog > .v-card > .v-card__text" ma vyssi specificitu (stejny nalez jako Items.vue/Stones.vue) */
.use-item-dialog-content {
    padding-bottom: var(--responsive-dialog-close-clearance) !important;
}
@media (min-width: 960px) {
    .use-item-dialog-content {
        padding-bottom: 16px !important;
    }
}

.use-item-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 4px;
    justify-content: center;
}

.use-item-grid__item {
    position: relative;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    width: clamp(70px, 22cqi, 110px);
    padding: 6px 2px;
    border-radius: 6px;
    background-color: rgba(0, 0, 0, .05);
    border: 1px solid rgba(0, 0, 0, .08);
}

.use-item-grid__name {
    font-size: 11px;
    text-align: center;
    line-height: 1.2;
}

/* Wrap kolem obrazku - count badge se pozicuje vuci nemu (40x40, stabilni
   vyska), ne vuci cele dlazdici, jejiz vyska kolisa podle delky nazvu
   (stejny nalez a stejna oprava jako Items.vue). */
.use-item-grid__icon-wrap {
    position: relative;
    width: 40px;
    height: 40px;
}

.use-item-grid__count {
    position: absolute;
    bottom: -4px;
    right: -6px;
    font-size: 11px;
    font-weight: 700;
    background-color: rgba(0, 0, 0, .55);
    color: #fff;
    border-radius: 999px;
    padding: 0 5px;
    line-height: 1.5;
}
</style>
